<script setup lang="ts">
import type { FieldSize } from '@/models/main';

import { computed, ref } from 'vue';
import _capitalize from 'lodash/capitalize';

import { stateShortNames } from '@/utils/PortalConstants';
import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import useField from '@/composables/useField';

import listItemStyles from '@/styles/listItem.module.scss';

export interface Props {
  modelValue?: string[] | null;
  size?: FieldSize;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'medium',
});
const emit = defineEmits<{
  'update:modelValue': [value: (typeof props)['modelValue']];
}>();

const fieldValue = ref(props.modelValue);
const field = ref<HTMLFormElement | null>(null);

useField(props, fieldValue, (newFieldValue) => {
  emit('update:modelValue', newFieldValue as (typeof props)['modelValue']);
});
const { handleEnterKeydown } = useAutocompleteField(fieldValue, null, field);

const sizeClass = computed(() => 'size' + _capitalize(props.size));

const allSelected = computed(() => {
  if (!fieldValue.value) return false;
  return fieldValue.value.length === stateShortNames.length;
});
const someSelected = computed(() => {
  if (!fieldValue.value) return false;
  return fieldValue.value.length > 0;
});

function toggleAll(): void {
  if (allSelected.value) {
    fieldValue.value = [];
  } else {
    fieldValue.value = stateShortNames.slice();
  }
}

function getOneSelection(item: string, index: number): string {
  let text = '';
  if (!allSelected.value) {
    text = `${item}${index + 1 < (fieldValue.value?.length ?? 0) ? ',' : ' '}`;
  } else {
    text = index === 0 ? 'All' : '';
  }
  return text;
}
</script>

<template>
  <v-select
    ref="field"
    v-model="fieldValue"
    label="States"
    :items="stateShortNames"
    :class="['g-v-text-field', $style.stateMultiSelectField, $style[sizeClass]]"
    data-testid="stateMultiSelectField"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    clearable
    menu-icon="mdi-chevron-down"
    multiple
    @keydown.enter="handleEnterKeydown"
  >
    <template #selection="{ item, index }">
      {{ getOneSelection(item.title, index) }}
    </template>
    <template #no-data>
      <NoDataSelectVListItem />
    </template>
    <template #prepend-item>
      <v-list-item
        :active="allSelected"
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        :ripple="false"
        title="All"
        @click="toggleAll"
      >
        <template #prepend>
          <KCheckboxField
            tabindex="-1"
            :indeterminate="someSelected && !allSelected"
            :model-value="someSelected"
            hide-details
            readonly
          />
        </template>
      </v-list-item>
      <v-divider class="mt-2"></v-divider>
    </template>
    <template #item="itemData">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        :ripple="false"
        v-bind="itemData.props"
      >
        <template #prepend="{ isSelected }">
          <v-list-item-action>
            <KCheckboxField
              tabindex="-1"
              :model-value="isSelected"
              hide-details
            />
          </v-list-item-action>
        </template>
        <template #title="{ title }">
          <v-icon
            v-if="itemData.item.props.titleIcon"
            :class="listItemStyles.titleIcon"
            :icon="itemData.item.props.titleIcon"
          >
          </v-icon>
          {{ title }}
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
}

.stateMultiSelectField {
  :global {
    .v-field__input {
      min-height: 40px;
      font-size: 16px;
      line-height: 22px;
      padding: 9px 16px 7px 16px;
    }
    .v-select__selection {
      margin-top: 0;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.sizeSmall {
  :global {
    .v-field__input {
      min-height: 36px;
      font-size: 14px;
      line-height: 20px;
    }
  }
}

.sizeLarge {
  :global {
    .v-field__input {
      min-height: 48px;
      font-size: 18px;
      line-height: 26px;
      padding: 11px 16px 9px 16px;
    }
  }
}
</style>
